/**
 * @file use-click-outside.ts
 *
 * Hook that invokes a callback when a `mousedown` lands outside the referenced
 * element. Used by transient overlays (the product detail drawer, filter
 * dropdowns) to dismiss themselves. The listener is only attached while
 * `active`, and the latest callback is read through a ref so passing an inline
 * handler does not re-subscribe on every render.
 */

import { useEffect, useRef, type RefObject } from 'react';

/**
 * Calls `onOutside` when a pointer press occurs outside `ref` while `active`.
 *
 * @param ref - Ref to the element treated as "inside".
 * @param onOutside - Invoked on an outside `mousedown`.
 * @param active - Whether the listener is attached (defaults to `true`).
 */
export function useClickOutside<T extends HTMLElement>(
  ref: RefObject<T | null>,
  onOutside: () => void,
  active = true,
): void {
  const callbackRef = useRef(onOutside);
  callbackRef.current = onOutside;

  useEffect(() => {
    if (!active) {
      return;
    }
    const handle = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        callbackRef.current();
      }
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [ref, active]);
}
